import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { type KeyboardEvent, type MutableRefObject, useCallback } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { Macro, RecordFolder } from 'src/graphql';
import {
  FAVORITES_ID,
  getFolderEntryId,
  getMacroEntryId,
  UNCATEGORIZED_ID,
} from 'src/pages/Conversations/Chat/Macros/macros-modal/constants';
import { GroupedFolders } from 'src/pages/Conversations/Chat/Macros/macros-modal/types';
import { MacroListItem } from './macro-list-item';

interface MacroFolderListProps {
  groupedFolders: GroupedFolders;
  expandedFolderIds: Set<string>;
  onToggleFolder: (folderId: string) => void;
  isManageMode: boolean;
  canManageMacros: boolean;
  canEditPlatformMacros: boolean;
  currentUserId: string | undefined;
  deletingFolderId: string | null;
  deleteFolderLoading: boolean;
  onDeleteFolder: (folder: RecordFolder) => void | Promise<void>;
  editingFolderId: string | null;
  editingFolderName: string;
  updateFolderLoading: boolean;
  onStartRenameFolder: (folder: RecordFolder) => void;
  onCancelRenameFolder: () => void;
  onRenameFolder: (folder: RecordFolder) => void;
  onEditFolderNameChange: (value: string) => void;
  deletingMacroId: string | null;
  favoriteMacroIds: Set<string>;
  isMacroFavoritable: (macro: Macro) => boolean;
  onToggleFavoriteMacro: (macro: Macro) => void;
  onSelectMacro: (macro: Macro) => void | Promise<void>;
  onDeleteMacro: (macro: Macro) => void | Promise<void>;
  onListKeyDown: (event: KeyboardEvent<HTMLUListElement>) => void;
  highlightedIndex: number;
  activeEntryId: string | null;
  visibleEntryIndexById: Map<string, number>;
  listItemRefs: MutableRefObject<(HTMLDivElement | null)[]>;
}

export const MacroFolderList = ({
  groupedFolders,
  expandedFolderIds,
  onToggleFolder,
  isManageMode,
  canManageMacros,
  canEditPlatformMacros,
  currentUserId,
  deletingFolderId,
  deleteFolderLoading,
  onDeleteFolder,
  editingFolderId,
  editingFolderName,
  updateFolderLoading,
  onStartRenameFolder,
  onCancelRenameFolder,
  onRenameFolder,
  onEditFolderNameChange,
  deletingMacroId,
  favoriteMacroIds,
  isMacroFavoritable,
  onToggleFavoriteMacro,
  onSelectMacro,
  onDeleteMacro,
  onListKeyDown,
  highlightedIndex,
  activeEntryId,
  visibleEntryIndexById,
  listItemRefs,
}: MacroFolderListProps) => {
  const uncategorizedEntryId = getFolderEntryId(UNCATEGORIZED_ID);
  const uncategorizedListIndex = visibleEntryIndexById.get(uncategorizedEntryId) ?? -1;
  const isUncategorizedHighlighted = uncategorizedListIndex === highlightedIndex;

  const renderMacro = useCallback(
    (macro: Macro, scopeId: string) => {
      const entryId = getMacroEntryId(macro.id, scopeId);
      const listIndex = visibleEntryIndexById.get(entryId) ?? -1;
      const isHighlighted = listIndex === highlightedIndex;
      return (
        <MacroListItem
          key={entryId}
          entryId={entryId}
          macro={macro}
          isFavorite={favoriteMacroIds.has(macro.id)}
          canToggleFavorite={isMacroFavoritable(macro)}
          isHighlighted={isHighlighted}
          isManageMode={isManageMode}
          canEditPlatformMacros={canEditPlatformMacros}
          isDeleting={deletingMacroId === macro.id}
          canManageMacros={canManageMacros}
          currentUserId={currentUserId}
          onToggleFavorite={() => onToggleFavoriteMacro(macro)}
          onSelect={() => void onSelectMacro(macro)}
          onEdit={() => void onSelectMacro(macro)}
          onDelete={() => void onDeleteMacro(macro)}
          ref={(el) => {
            if (listIndex >= 0) {
              listItemRefs.current[listIndex] = el;
            }
          }}
        />
      );
    },
    [
      canManageMacros,
      currentUserId,
      deletingMacroId,
      favoriteMacroIds,
      highlightedIndex,
      isMacroFavoritable,
      isManageMode,
      canEditPlatformMacros,
      listItemRefs,
      onDeleteMacro,
      onToggleFavoriteMacro,
      onSelectMacro,
      visibleEntryIndexById,
    ],
  );

  const handleRenameKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>, folder: RecordFolder) => {
      event.stopPropagation();
      if (event.key === 'Enter') {
        event.preventDefault();
        onRenameFolder(folder);
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancelRenameFolder();
      }
    },
    [onCancelRenameFolder, onRenameFolder],
  );

  return (
    <List
      tabIndex={0}
      role="listbox"
      aria-label="Macros list"
      aria-activedescendant={activeEntryId ?? undefined}
      onKeyDown={onListKeyDown}
      sx={{
        overflowY: 'auto',
        padding: 0,
        flex: 1,
      }}
    >
      {groupedFolders.favoritesSection ? (
        <Stack>
          <ListItemButton
            id={getFolderEntryId(FAVORITES_ID)}
            selected={visibleEntryIndexById.get(getFolderEntryId(FAVORITES_ID)) === highlightedIndex}
            sx={{
              borderBottom: (theme) =>
                `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
              paddingY: 1.5,
              paddingX: 3,
              gap: 1.5,
            }}
          >
            <DynamicIcon name="star" filled sx={{ color: 'warning.main' }} />
            <Box flex={1} minWidth={0}>
              <ListItemText
                primary={
                  <Typography variant="subtitle2" fontWeight={500} noWrap>
                    {groupedFolders.favoritesSection.name}
                  </Typography>
                }
              />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              minWidth={16}
              textAlign="right"
            >
              {groupedFolders.favoritesSection.macros.length}
            </Typography>
          </ListItemButton>
          {groupedFolders.favoritesSection.macros.map((macro) =>
            renderMacro(macro, FAVORITES_ID),
          )}
        </Stack>
      ) : null}
      {groupedFolders.folderSections.map((section) => {
        const folderEntryId = getFolderEntryId(section.id);
        const listIndex = visibleEntryIndexById.get(folderEntryId) ?? -1;
        const isHighlighted = listIndex === highlightedIndex;
        const isExpanded = expandedFolderIds.has(section.id);
        const isPlatformFolder = section.folder.partnerId === PLATFORM_PARTNER_ID;
        const canEditFolder =
          isManageMode &&
          canManageMacros &&
          (canEditPlatformMacros || section.folder.partnerId !== PLATFORM_PARTNER_ID);
        const isDeleting = deletingFolderId === section.id;
        const isEditing = editingFolderId === section.id;
        const folderCount = section.macros.length;

        return (
          <Stack key={section.id}>
            <ListItemButton
              onClick={() => {
                if (!isEditing) {
                  onToggleFolder(section.id);
                }
              }}
              id={folderEntryId}
              selected={isHighlighted}
              ref={(el) => {
                if (listIndex >= 0) {
                  listItemRefs.current[listIndex] = el;
                }
              }}
              sx={{
                borderBottom: (theme) =>
                  `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                paddingY: 1.5,
                paddingX: 3,
                gap: 1.5,
              }}
            >
              <DynamicIcon name={isExpanded ? 'expand_more' : 'chevron_right'} />
              <DynamicIcon name={section.folder.icon || 'folder'} weight={400} />
              <Box flex={1} minWidth={0}>
                {isEditing ? (
                  <TextField
                    autoFocus
                    size="small"
                    value={editingFolderName}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => onEditFolderNameChange(event.target.value)}
                    onKeyDown={(event) => handleRenameKeyDown(event, section.folder)}
                    placeholder="Folder name"
                    fullWidth
                  />
                ) : (
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" fontWeight={500} noWrap>
                        {section.name}
                      </Typography>
                    }
                  />
                )}
              </Box>
              <Stack direction="row" gap={1} alignItems="center">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  minWidth={16}
                  textAlign="right"
                >
                  {folderCount}
                </Typography>
                {isPlatformFolder ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<DynamicIcon name="shield" />}
                    label="Platform"
                  />
                ) : null}
              </Stack>
              {canEditFolder ? (
                <Stack direction="row" alignItems="center" gap={0.5}>
                  {isEditing ? (
                    <>
                      <Tooltip title="Save name">
                        <span>
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              onRenameFolder(section.folder);
                            }}
                            disabled={updateFolderLoading}
                          >
                            {updateFolderLoading ? (
                              <CircularProgress size={18} />
                            ) : (
                              <DynamicIcon name="check" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            onCancelRenameFolder();
                          }}
                        >
                          <DynamicIcon name="close" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Rename folder">
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            onStartRenameFolder(section.folder);
                          }}
                        >
                          <DynamicIcon name="edit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete folder">
                        <span>
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              void onDeleteFolder(section.folder);
                            }}
                            disabled={deleteFolderLoading || isDeleting}
                          >
                            {isDeleting ? (
                              <CircularProgress size={18} />
                            ) : (
                              <DynamicIcon name="delete" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </Stack>
              ) : null}
            </ListItemButton>
            <Collapse in={isExpanded} unmountOnExit>
              {section.macros.map((macro) => renderMacro(macro, section.id))}
            </Collapse>
          </Stack>
        );
      })}
      {groupedFolders.uncategorizedSection ? (
        <Stack>
          <ListItemButton
            id={uncategorizedEntryId}
            selected={isUncategorizedHighlighted}
            ref={(el) => {
              if (uncategorizedListIndex >= 0) {
                listItemRefs.current[uncategorizedListIndex] = el;
              }
            }}
            sx={{
              borderBottom: (theme) =>
                `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
              paddingY: 1.5,
              paddingX: 3,
              gap: 1.5,
            }}
          >
            <DynamicIcon name="folder" weight={400} />
            <Box flex={1} minWidth={0}>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    noWrap
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {groupedFolders.uncategorizedSection.name}
                  </Typography>
                }
              />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              minWidth={16}
              textAlign="right"
            >
              {groupedFolders.uncategorizedSection.macros.length}
            </Typography>
          </ListItemButton>
          {groupedFolders.uncategorizedSection.macros.map((macro) =>
            renderMacro(macro, UNCATEGORIZED_ID),
          )}
        </Stack>
      ) : null}
    </List>
  );
};
